import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { take, switchMap, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

import { AuthRepository } from './auth.repository';
import { environment } from '@env/environment';
import { PresenceService } from '@app/services/presence.service';

@Injectable({ providedIn: 'root' })
export class JwtRefreshService {
  private refreshInProgress = false;
  private refreshSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private authRepository: AuthRepository,
    private router: Router,
    private presenceService: PresenceService
  ) {}

  /**
   * Refresh the JWT token using the refresh token
   * @returns Promise<boolean> - true if refresh successful, false otherwise
   */
  async refreshToken(): Promise<boolean> {
    if (this.refreshInProgress) {
      return this.refreshSubject.pipe(take(1)).toPromise() as Promise<boolean>;
    }

    this.refreshInProgress = true;
    const refreshToken = this.authRepository.getRefreshToken();

    if (!refreshToken) {
      console.error('No refresh token available');
      this.handleRefreshFailure();
      return false;
    }

    try {
      const response = await this.http.post<any>(`${environment.serverUrl}/v5/refresh-token`, {
        refresh_token: refreshToken
      }).toPromise();

      if (response && response.access_token) {
        // Update the auth store with new tokens
        const currentAuth = this.authRepository.getValue();
        this.authRepository.save({
          ...currentAuth,
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          expires_in: response.expires_in
        });
        
        console.log('Token refresh successful');
        this.refreshInProgress = false;
        this.refreshSubject.next(true);
        return true;
      } else {
        console.error('Invalid response from refresh endpoint');
        this.handleRefreshFailure();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.handleRefreshFailure();
      return false;
    }
  }

  /**
   * Get the current refresh status as an observable
   * @returns Observable<boolean>
   */
  getRefreshStatus(): Observable<boolean> {
    return this.refreshSubject.asObservable();
  }

  /**
   * Check if a refresh is currently in progress
   * @returns boolean
   */
  isRefreshInProgress(): boolean {
    return this.refreshInProgress;
  }

  /**
   * Handle refresh failure by logging out the user
   */
  private handleRefreshFailure(): void {
    console.log('Handling refresh failure - logging out user');
    this.presenceService.stopPresence(true);
    this.authRepository.logout();
    this.router.navigate(['/login']);
    this.refreshInProgress = false;
    this.refreshSubject.next(false);
  }

  /**
   * Reset the refresh state
   */
  resetRefreshState(): void {
    this.refreshInProgress = false;
    this.refreshSubject.next(false);
  }
}








